import { css } from '@emotion/react';
import { useTranslation } from '@frontend/i18n';
import { Icon } from '@frontend/icons';
import { breakpoints } from '@frontend/responsiveness';
import { theme } from '@frontend/theme';
import { Button, ModalControlTriggerProps, Text } from '@frontend/design-system';
import { EIVDashboardTrialWidgetTrackingIds } from '../../../../../constants/tracking-ids';

type OnboardingItemType = 'CONNECT_PAYERS' | 'FAVORITE_CDT_CODES' | 'SET_UP_DELTA_DENTAL_MFA';

type OnboardingItemProps = {
  title: string;
  itemType: OnboardingItemType;
  isOnboardingCompleted: boolean;
  triggerProps: ModalControlTriggerProps;
  icon?: React.ReactNode;
};

const trackingIDMap: Record<OnboardingItemType, string> = {
  CONNECT_PAYERS: EIVDashboardTrialWidgetTrackingIds.setupConnectPayersAction,
  FAVORITE_CDT_CODES: EIVDashboardTrialWidgetTrackingIds.setupFavoriteCDTCodesAction,
  SET_UP_DELTA_DENTAL_MFA: EIVDashboardTrialWidgetTrackingIds.setupMFAAction,
};

export const OnboardingItem = ({ title, isOnboardingCompleted, itemType, icon, triggerProps }: OnboardingItemProps) => {
  const { t } = useTranslation('featurePromotion', { keyPrefix: 'weave-insurance-eligibility' });
  return (
    <div css={onboardingItemStyles}>
      <div css={onBoardingItemContentStyles}>
        {icon}
        <Text weight='bold' size='medium'>
          {title}
        </Text>
      </div>
      {isOnboardingCompleted ? (
        <Icon name='success' size={16} color='success' />
      ) : (
        <Button variant='tertiary' {...triggerProps} trackingId={trackingIDMap[itemType]}>
          {t('Setup')}
        </Button>
      )}
    </div>
  );
};

const onboardingItemStyles = css({
  display: 'flex',
  height: theme.spacing(6.5),
  flex: 1,
  flexDirection: 'row',
  justifyContent: 'space-between',
  alignItems: 'center',
  padding: theme.spacing(1, 2),
  border: '2px',
  borderRadius: theme.borderRadius.small,
  boxShadow: theme.shadows.light,
  backdropFilter: 'blur(4px)',
  [`@container (max-width: ${breakpoints.mobile.max}px)`]: {
    flex: 'unset',
    width: '100%',
  },
});

const onBoardingItemContentStyles = css({
  display: 'flex',
  flexDirection: 'row',
  alignItems: 'center',
  gap: theme.spacing(0.5),
});
